import { makeObservable, observable, action, computed } from 'mobx'
import axios from 'axios'
import { BaseStore } from './BaseStore'
import type { User } from '@core/models'

export class UserStore extends BaseStore {
  users: Map<string, User> = new Map()
  currentUser: User | null = null

  constructor() {
    super()
    makeObservable(this, {
      users: observable,
      currentUser: observable,
      getUsers: action,
      getUser: action,
      createUser: action,
      updateUser: action,
      deleteUser: action,
      usersArray: computed
    })
  }

  async getUsers(tenantId: string) {
    const result = await this.handleApiCall(async () => {
      const response = await axios.get<User[]>(
        this.apiPath(`v1/tenants/${tenantId}/users`),
        { headers: this.generateFetchHeader() }
      )
      return response.data
    })

    if (result) {
      this.users.clear()
      result.forEach(user => {
        this.users.set(user.id, user)
      })
      return true
    }
    return false
  }

  async getUser(tenantId: string, userId: string) {
    const result = await this.handleApiCall(async () => {
      const response = await axios.get<User>(
        this.apiPath(`v1/tenants/${tenantId}/users/${userId}`),
        { headers: this.generateFetchHeader() }
      )
      return response.data
    })

    if (result) {
      this.currentUser = result
      this.users.set(result.id, result)
      return true
    }
    return false
  }

  async createUser(tenantId: string, data: Partial<User> & { password: string }) {
    const result = await this.handleApiCall(async () => {
      await axios.post(
        this.apiPath(`v1/tenants/${tenantId}/users`),
        data,
        { headers: this.generateFetchHeader() }
      )
      return true
    })

    if (result) {
      // ユーザー一覧を再取得
      await this.getUsers(tenantId)
      this.tryShowToast('ユーザーを作成しました', 'success')
      return true
    }

    this.tryShowToast('ユーザーの作成に失敗しました', 'error')
    return false
  }

  async updateUser(tenantId: string, userId: string, data: Partial<User>) {
    const result = await this.handleApiCall(async () => {
      await axios.put(
        this.apiPath(`v1/tenants/${tenantId}/users/${userId}`),
        data,
        { headers: this.generateFetchHeader() }
      )
      return true
    })

    if (result) {
      // 更新されたユーザー情報を再取得
      await this.getUser(tenantId, userId)
      this.tryShowToast('ユーザー情報を更新しました', 'success')
      return true
    }

    this.tryShowToast('ユーザー情報の更新に失敗しました', 'error')
    return false
  }

  async deleteUser(tenantId: string, userId: string) {
    const result = await this.handleApiCall(async () => {
      await axios.delete(
        this.apiPath(`v1/tenants/${tenantId}/users/${userId}`),
        { headers: this.generateFetchHeader() }
      )
      return true
    })

    if (result) {
      this.users.delete(userId)
      if (this.currentUser?.id === userId) {
        this.currentUser = null
      }
      this.tryShowToast('ユーザーを削除しました', 'success')
      return true
    }

    this.tryShowToast('ユーザーの削除に失敗しました', 'error')
    return false
  }

  get usersArray() {
    return Array.from(this.users.values())
  }
}